<template>
  <div class="edit-profile-panel">
    <div class="edit-avatar-wrapper flex-wrapper">
      <div class="avatar-wrapper" :class="{edit: avatarEditable}" :style="{ background: userAvatarColor }">
        <avatar :username="fullname" :backgroundColor="userAvatarColor || '#ff9874'" v-if="userAvatarVisible"/>
        <rt-picture :src="avatarIconPath" v-else/>
        <primary-button class="btn-normal btn-cancel" @click="handleCancelAvatar"
                        v-if="userAvatarIcon || userAvatarColor">
          <rt-picture class="icon-cancel" src="/assets/app/class/icon-cancel.svg"/>
        </primary-button>
      </div>
      <primary-button class="btn-edit-avatar" ref="avatarBtn" @click="dropdownVisible=true">{{
          avatarButtonName
        }}
      </primary-button>
      <div class="avatars-dropdown" ref="dropdown" v-if="dropdownVisible">
        <div class="avatar-group">
          <h2 class="group-name">Choose an avatar</h2>
          <div class="avatar-icons-wrapper">
            <div class="avatar-icons">
              <div class="avatar-item" :class="{ active: avatarIcon === 'default' }" @click="avatarIcon='default'">
                <avatar :username="fullname" :backgroundColor="avatarColor || '#ff9874'"/>
              </div>
              <div class="avatar-item" :class="{ active: avatarIcon === icon }" :key="idx"
                   v-for="(icon, idx) in avatarIcons" @click="avatarIcon=icon">
                <rt-picture :src="icon"/>
              </div>
            </div>
          </div>
        </div>
        <div class="avatar-group">
          <h2 class="group-name">Choose background color</h2>
          <div class="avatar-icons-wrapper">
            <div class="avatar-icons">
              <div class="avatar-item" :class="{ active: avatarColor === color }" :key="idx + avatarIcons.length"
                   :style="{ background: color }" v-for="(color, idx) in avatarColors"
                   @click="avatarColor = color"></div>
            </div>
          </div>
        </div>
        <primary-button class="btn-confirm" @click="handleConfirmAvatar">Confirm</primary-button>
      </div>
    </div>
    <div class="profile-setting-wrapper">
      <rt-input v-model="userName" ref="username" placeholder="User Name" floatingLabel="Username"/>
      <rt-input v-model="firstName" ref="first" :placeholder="firstNameHint" :floatingLabel="firstNameFloatingLabel"/>
      <div class="last-name-wrapper">
        <rt-input v-model="lastName" ref="last" placeholder="Real Last Name" floatingLabel="Last name"/>
        <p class="name-error" v-if="nameError">{{ nameError }}</p>
      </div>
      <div class="setting-divider"></div>
      <div class="email-wrapper">
        <rt-input class="input-email" v-model="email" ref="email" :placeholder="emailHint"
                  :floatingLabel="emailFloatingLabel"/>
        <p class="email-error" v-if="emailError">{{ emailError }}</p>
      </div>
      <div class="setting-divider" v-if="studentMode"></div>
      <div class="age-field-wrapper">
        <rt-select class="age-picker" :options="ageOptions" :value="ageOptionFrom(age)" type="floating" floatLabel="Age"
                   @change="handleChangeAge" v-if="studentMode"/>
        <p class="age-error" v-if="ageError">{{ ageError }}</p>
      </div>
      <div class="setting-divider"></div>
      <div class="setting-timezone">
        <rt-select class="timezone-picker" :options="timeZoneOptions" :value="selectedTimeZone" ref="timezone"
                   type="floating" floatLabel="Time Zone" @change="handleChangeTimeZone"/>
        <p class="current-timezone">{{ currTimeZone ? currTimeZone : 'No timezone currently selected' }}</p>
      </div>
      <div style="text-align: right">
        <a target="_blank" rel="privacy-policy" href="https://readtheory.org/privacy-policy/">Privacy Policy</a>
      </div>
      <div class="setting-divider" v-if="subscriptionVisible"></div>
      <div class="setting-subscription" v-if="subscriptionVisible">
        <p class="subscription-name">Subscription Information</p>
        <primary-button class="btn-billing" @click="handleUpdateBilling">Update Billing Information / View Past
          Invoices
        </primary-button>
      </div>
    </div>
    <div class="setting-panel-footer">
      <primary-button class="btn-close" @click="$emit('close', profileSaved)">Close</primary-button>
      <primary-button class="btn-save" @click="handleSaveProfile">Save</primary-button>
    </div>
  </div>
</template>

<script>
import _ from 'lodash';
import Avatar from 'vue-avatar';
import RtPicture from '../Picture.vue';
import PrimaryButton from '../PrimaryButton.vue';
import RtInput from '../Input.vue';
import RtSelect from '../Select.vue';
import {validateEmail} from '../../signup/validators';
import {AVATAR_BG_COLORS} from 'config';
import {getUser, getStudent, isStudent, getClasses, isSubscriptionActive} from 'common/UserService';
import {getBillingPortalUrl, updateUser} from 'common/api';
import Utils from 'common/Utils';
import Toast from 'common/Toast';
import log from 'common/Logger';
import {isCoppaStudent} from 'common/coppa';

export default {
  data: function () {
    return {
      userName: '',
      firstName: '',
      lastName: '',
      nameError: '',
      email: '',
      emailError: '',
      age: null,
      ageError: '',
      userAvatarIcon: null,
      userAvatarColor: null,
      ageOptions: [],
      dropdownVisible: false,
      avatarIcons: [],
      avatarIcon: null,
      avatarColors: AVATAR_BG_COLORS,
      avatarColor: null,
      studentMode: false,
      studentInitial: false,
      loading: false,
      profileSaved: false,
      timeZones: [],
      timeZoneOptions: [],
      currTimeZone: null,
      selectedTimeZone: null
    }
  },
  components: {
    Avatar,
    RtPicture,
    PrimaryButton,
    RtInput,
    RtSelect
  },
  computed: {
    firstNameHint() {
      return !this.studentInitial ? 'Real First Name' : 'First Name Initial';
    },
    firstNameFloatingLabel() {
      return !this.studentInitial ? 'First Name' : 'First Name Initial';
    },
    emailHint() {
      if (!this.studentInitial) {
        return 'Email Address';
      } else {
        const studentClasses = getClasses();
        return _.isEmpty(studentClasses) ? 'School Email Address' : 'Parent Email Address';
      }
    },
    emailFloatingLabel() {
      if (!this.studentInitial) {
        return 'Email';
      } else {
        const studentClasses = getClasses();
        return _.isEmpty(studentClasses) ? 'School Email' : 'Parent Email';
      }
    },
    fullname() {
      return `${this.firstName} ${this.lastName}`;
    },
    userAvatarVisible() {
      return this.userAvatarIcon === 'default' || (!this.userAvatarIcon && this.userAvatarColor);
    },
    avatarEditable() {
      return this.userAvatarIcon || this.userAvatarColor;
    },
    avatarIconPath() {
      if (!this.userAvatarIcon) {
        return '/assets/app/quiz/icon-ghost.svg';
      } else {
        return this.userAvatarIcon;
      }
    },
    avatarButtonName() {
      return this.avatarEditable ? 'Edit Avatar' : 'Create An Avatar';
    },
    subscriptionVisible() {
      if (!_.isEmpty(getClasses()) && isStudent()) {
        return false;
      } else {
        return isSubscriptionActive();
      }
      ;
    },
    localTimeZone() {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    }
  },
  watch: {
    firstName(newVal, oldVal) {
      if (this.studentInitial) {
        if (!!oldVal && oldVal.length === 2) {
          this.firstName = '';
        } else if (!!newVal && newVal.length === 1) {
          this.firstName = newVal.toUpperCase() + '.';
        }
      }
    }
  },
  beforeMount() {
    window.addEventListener('click', this.handleClickWindow);
  },
  beforeDestroy() {
    window.removeEventListener('click', this.handleClickWindow);
  },
  mounted() {
    this.studentInitial = isCoppaStudent();
    for (let i = 3; i < 22; i++) {
      let option = 'Select age';
      if (i > 3) {
        option = i < 21 ? i.toString() : '21 or older';
      }
      this.ageOptions.push(option);
    }

    const user = getUser();
    if (user) {
      if (user.username) {
        this.userName = user.username;
        this.$refs.username.updateValue(user.username);
      }
      if (user.firstName) {
        this.firstName = user.firstName;
        this.$refs.first.updateValue(user.firstName);
      }
      if (user.lastName) {
        this.lastName = user.lastName;
        this.$refs.last.updateValue(user.lastName);
      }
      if (user.email) {
        this.email = user.email;
        this.$refs.email.updateValue(user.email);
      }
      const student = getStudent();
      this.studentMode = !!student;
      if (student && student.dob) {
        this.age = Utils.getAgeFromBirthdate(student.dob);
      }
    }

    // avatar setup
    const userSettings = this.getUserSettings();
    if (userSettings) {
      if (userSettings.avatarIcon) {
        this.avatarIcon = userSettings.avatarIcon;
        this.userAvatarIcon = userSettings.avatarIcon;
      }
      if (userSettings.avatarColor) {
        this.avatarColor = userSettings.avatarColor;
        this.userAvatarColor = userSettings.avatarColor;
      }
    }
    const avatarIcons = [];
    for (let i = 1; i < 45; i++) {
      avatarIcons.push(`/assets/app/quiz/animals/icon-animal-${i}.svg`);
    }
    this.avatarIcons = avatarIcons;
    this.setupTimeZones()
  },
  methods: {
    getUserSettings() {
      const user = getUser();
      const userSettings = user.userSettings ? JSON.parse(user.userSettings) : null;
      return userSettings;
    },
    ageOptionFrom() {
      return this.age ? this.age - 3 : null;
    },
    setupTimeZones() {
      const user = getUser();
      this.currTimeZone = user && user.timezone ? user.timezone : null;
      const tzOptions = [];
      const allTimeZones = Intl.supportedValuesOf('timeZone');
      allTimeZones.forEach((timeZone, idx) => {
        const timeZoneName = Intl.DateTimeFormat('fr', {
          timeZoneName: "short",
          timeZone,
        }).formatToParts()
            .find((i) => i.type === "timeZoneName").value;

        if (this.currTimeZone === timeZone) {
          this.selectedTimeZone = idx + 1;
        }

        const utcOffsetSplits = timeZoneName.split('UTC');
        let utcOffset = utcOffsetSplits.length > 1 ? utcOffsetSplits[1] : null;
        if (utcOffset) {
          let offsetNum = parseInt(utcOffset);
          if (offsetNum > 0 && offsetNum < 10) {
            utcOffset = `+0${offsetNum}`;
          } else if (offsetNum > -10 && offsetNum < 0) {
            utcOffset = `-0${Math.abs(offsetNum)}`;
          }
        }
        const timeZoneOption = `${timeZone}${utcOffset ? ` (${utcOffset}:00)` : ' (+00:00)'}`;
        tzOptions.push(timeZoneOption);
      });
      this.timeZones = [
        null,
        ...allTimeZones
      ];
      this.timeZoneOptions = [
        'Select Time Zone',
        ...tzOptions
      ];
    },
    handleClickWindow(e) {
      if (this.$refs.avatarBtn && e.target !== this.$refs.avatarBtn.$el && this.$refs.dropdown && !this.$refs.dropdown.contains(e.target)) {
        this.dropdownVisible = false;
      }
    },
    handleChangeAge(age) {
      if (age > 0) {
        this.age = age + 3;
      } else {
        this.age = null;
      }
      this.studentInitial = this.age < 13;
    },
    handleChangeTimeZone(timezone) {
      this.selectedTimeZone = timezone;
    },
    handleCancelAvatar() {
      this.avatarIcon = null;
      this.avatarColor = null;
      this.userAvatarIcon = null;
      this.userAvatarColor = null;
    },
    handleConfirmAvatar() {
      this.userAvatarIcon = this.avatarIcon;
      this.userAvatarColor = this.avatarColor;
      this.dropdownVisible = false;
    },
    async validateSubmit() {
      if (!this.userName) {
        this.$refs.username.focus();
        this.$refs.username.$el.classList.add('error')
        this.nameError = 'Username is required';
        return false;
      } else {
        this.nameError = '';
        this.$refs.username.$el.classList.remove('error')
      }

      const emailValid = await validateEmail(this.email, false);
      if (emailValid !== true) {
        this.$refs.email.focus();
        this.$refs.email.$el.classList.add('error')
        this.emailError = 'Email is invalid';
        return false;
      } else {
        this.emailError = '';
        this.$refs.email.$el.classList.remove('error')
      }

      if (this.studentMode) {
        this.ageError = '';
        if (!this.age) {
          this.ageError = 'The age should be selected';
          return false;
        }
      }

      if (!this.selectedTimeZone) {
        this.$refs.timezone.$refs.select.focus();
        this.$refs.timezone.$refs.select.classList.add('error');
        return false;
      } else {
        this.$refs.timezone.$refs.select.classList.remove('error');
      }

      return true;
    },
    async handleUpdateBilling() {
      const user = getUser();
      const userSettings = user.userSettings ? JSON.parse(user.userSettings) : null;
      if (!userSettings || !userSettings.subscription) {
        this.$emit('onSubscription');
      } else {
        const customerId = userSettings.subscription?.customer_id;
        const res = await getBillingPortalUrl(customerId, window.location.href);
        if (res.success) {
          window.location.href = res.response.url;
        } else {
          log.error('no billing url');
          return;
        }
      }
    },
    async handleSaveProfile() {
      this.profileSaved = false;
      const isValid = await this.validateSubmit();
      if (!this.loading && isValid) {
        this.loading = true;
        let userSettings = this.getUserSettings() || {};
        userSettings.avatarIcon = this.userAvatarIcon;
        userSettings.avatarColor = this.userAvatarColor;
        const dob = this.age ? Utils.getBirthdateFromAge(this.age) : '';
        const timeZone = this.selectedTimeZone ? this.timeZones[this.selectedTimeZone] : null;
        const result = await updateUser(getUser().id, this.userName, this.firstName, this.lastName, this.email, dob, JSON.stringify(userSettings), timeZone);
        this.loading = false;
        if (result.success) {
          Toast.success(result.message);
          window.rt_bridge.session.user.userName = this.userName;
          window.rt_bridge.session.user.firstName = this.firstName;
          window.rt_bridge.session.user.lastName = this.lastName;
          window.rt_bridge.session.user.email = this.email;
          window.rt_bridge.session.user.userSettings = JSON.stringify(userSettings);
          this.currTimeZone = timeZone;
          if (this.studentMode) {
            window.rt_bridge.session.user.student.dob = dob;
          }
          this.profileSaved = true;
        } else {
          Toast.error(result.message);
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../variables';

*::-webkit-scrollbar {
  height: 6px;
}

.edit-profile-panel {
  .avatar-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 46px;
    height: 46px;
    border-radius: 50%;
    overflow: hidden;
    margin-right: 17px;
    position: relative;
    overflow: visible;

    .btn-cancel {
      display: none;
      position: absolute;
      top: -3px;
      right: -3px;
      margin: 0;

      .icon-cancel {
        width: 19px;
        height: 19px;
      }
    }

    .vue-avatar--wrapper {
      width: 46px !important;
      height: 46px !important;
    }

    &.edit {
      &:hover {
        border: 1.5px solid $light-blue2;

        .vue-avatar--wrapper {
          width: 42px !important;
          height: 42px !important;
        }

        .btn-cancel {
          display: block;
        }
      }
    }
  }

  .btn-edit-avatar {
    background: #ECF3FD;
    padding: 10px 16px;
    margin: 0;
    border: 0;
    height: auto;
    font-weight: 700;
    font-size: 12px;
    color: $light-blue2;
  }

  .edit-avatar-wrapper {
    position: relative;

    .avatars-dropdown {
      position: absolute;
      top: 100%;
      left: 0;
      right: 0;
      background: #fff;
      padding: 32px;
      margin-top: 9px;
      box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.12);
      border-radius: 5px;
      z-index: 1;
    }

    .avatar-group {
      padding-top: 40px;

      &:first-child {
        padding: 0;
      }

      .group-name {
        font-family: $title-font;
        font-weight: 700;
        font-size: 18px;
        color: $title-dark-grey;
      }

      .avatar-icons-wrapper {
        padding: 8px 0;
        overflow-x: auto;

        .avatar-icons {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          width: 1700px;

          .avatar-item {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 48px;
            height: 48px;
            margin: 8px 24px 8px 0;
            border-radius: 50%;
            position: relative;
            transition: all 0.2s ease;
            cursor: pointer;

            .vue-avatar--wrapper {
              width: 48px !important;
              height: 48px !important;
            }

            &.active {
              border: 2px solid $primary-blue;

              .vue-avatar--wrapper {
                width: 44px !important;
                height: 44px !important;
              }
            }

            &:not(.active) {
              &:hover {
                margin-top: -1px;
              }
            }

            .icon-check {
              position: absolute;
              right: -2px;
              bottom: -1px;
              width: 14px;
            }
          }
        }
      }

      &:nth-child(2) {
        .avatar-icons {
          width: 720px;
        }
      }
    }

    .primary-button {
      &.btn-confirm {
        margin: 13px 0 0 50%;
        transform: translateX(-50%);
      }
    }
  }

  .profile-setting-wrapper {
    padding-top: 32px;

    .rt-input__wrapper {
      margin-top: 8px;

      &:first-child, &.input-email {
        margin: 0;
      }
    }

    .last-name-wrapper, .email-wrapper, .age-field-wrapper {
      position: relative;

      .name-error, .email-error, .age-error {
        position: absolute;
        left: 0;
        top: calc(100% + 5px);
        font-size: 12px;
        color: $red;
      }
    }

    .last-name-wrapper {
      .rt-input__wrapper {
        margin-top: 8px;
      }
    }

    .setting-divider {
      width: 100%;
      margin: 32px 0;
      border: 1px solid $light-blue6;
    }

    .age-picker {
      width: 100%;

      ::v-deep(.floating-select-info) {
        font-weight: 700;
      }
    }

    .setting-timezone {
      .timezone-picker {
        width: 100%;
      }

      .current-timezone {
        margin-top: 10px;
        padding-left: 18px;
      }
    }

    .setting-subscription {
      .subscription-name {
        font-weight: 600;
      }

      .btn-billing {
        margin-top: 10px;
      }
    }
  }
}
</style>

